import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { categoryLabel } from "@/lib/affiliates";

interface Props {
  affiliate: {
    id: string;
    slug: string;
    name: string;
    tagline: string | null;
    location: string | null;
    category: string;
    services: string[];
    logo_url: string | null;
    featured?: boolean;
  };
  index?: number;
}

const AffiliateCard = ({ affiliate, index = 0 }: Props) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: Math.min(index * 0.04, 0.4) }}
    >
      <Link
        to={`/affiliates/${affiliate.slug}`}
        className="group block h-full bg-card border border-border rounded-lg p-6 hover:border-primary/40 hover:shadow-glow transition-all duration-300"
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
            {affiliate.logo_url ? (
              <img src={affiliate.logo_url} alt={`${affiliate.name} logo`} className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-lg font-bold text-primary">
                {affiliate.name.slice(0, 1)}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                {categoryLabel(affiliate.category)}
              </span>
              {affiliate.featured && (
                <span className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded">
                  Featured
                </span>
              )}
            </div>
            <h3 className="font-display font-semibold truncate">{affiliate.name}</h3>
            {affiliate.location && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <MapPin className="h-3 w-3" /> {affiliate.location}
              </p>
            )}
          </div>
        </div>
        {affiliate.tagline && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{affiliate.tagline}</p>
        )}
        {affiliate.services?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {affiliate.services.slice(0, 3).map((s) => (
              <span key={s} className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                {s}
              </span>
            ))}
            {affiliate.services.length > 3 && (
              <span className="text-[11px] text-muted-foreground">+{affiliate.services.length - 3}</span>
            )}
          </div>
        )}
        <span className="inline-flex items-center text-xs text-primary font-semibold group-hover:gap-2 transition-all gap-1">
          View provider <ArrowRight className="h-3 w-3" />
        </span>
      </Link>
    </motion.div>
  );
};

export default AffiliateCard;